/**
 * SmsConfigPage — Configuration de la passerelle SMS d'alerte (admin).
 *
 * Présente les 3 cas décidés :
 *   1. SMS désactivé — la commune n'en veut pas (garde une autre solution).
 *   2. Ma clé (mode 'commune') — la commune branche SA clé smsmode → prix
 *      coûtant strict, aucune marge, elle paie smsmode en direct.
 *   3. Passerelle VPC (mode 'passerelle') — clé en main, VPC avance et refacture
 *      avec un léger surcoût de gestion. Fonctionne sur un solde de crédits.
 *
 * Réservé grc.alerte.sms.config (super_admin / admin). Le crédit de solde
 * (recharge / amorçage) est réservé super_admin.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button, Input, Field, Alert, Badge, Card, SectionEyebrow, LogoMark } from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { MessageSquare, Check, X, KeyRound, Server, Ban } from 'lucide-react';
import { compressImageIfNeeded } from '@/lib/image-compress';

type Config = Awaited<ReturnType<typeof api.grc.sms.getConfig>>;
type Mode = 'commune' | 'passerelle';

function GrcTab({ active, onClick, children }: { active?: boolean; onClick?: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
        active ? 'bg-tech text-white' : 'text-ink-muted hover:bg-surface-muted'
      }`}
    >
      {children}
    </button>
  );
}

export function SmsConfigPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Champs éditables
  const [mode, setMode] = useState<Mode>('passerelle');
  const [expediteur, setExpediteur] = useState('Mairie');
  const [apiKey, setApiKey] = useState('');
  const [rechargeNb, setRechargeNb] = useState('');
  const [abonnes, setAbonnes] = useState<Awaited<ReturnType<typeof api.grc.sms.listAbonnes>>['abonnes']>([]);

  async function reloadAbonnes() {
    try {
      const r = await api.grc.sms.listAbonnes();
      setAbonnes(r.abonnes);
    } catch { /* silencieux */ }
  }

  async function reload() {
    setLoading(true); setError(null);
    try {
      const c = await api.grc.sms.getConfig();
      setConfig(c);
      setMode(c.modeCompte);
      setExpediteur(c.expediteur);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void reload(); void reloadAbonnes(); }, []);

  async function save(patch: Parameters<typeof api.grc.sms.updateConfig>[0], label: string) {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.grc.sms.updateConfig(patch);
      setSuccess(label);
      setApiKey('');
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur d’enregistrement');
    } finally {
      setSaving(false);
    }
  }

  async function recharger() {
    const nb = parseInt(rechargeNb, 10);
    if (!Number.isInteger(nb) || nb <= 0) { setError('Nombre de SMS à créditer invalide'); return; }
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.grc.sms.crediter(nb);
      setSuccess(`${nb} crédits SMS ajoutés`);
      setRechargeNb('');
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de crédit');
    } finally {
      setSaving(false);
    }
  }

  async function handleDesabonner(id: string) {
    try {
      await api.grc.sms.desabonnerParId(id);
      await reloadAbonnes();
      setSuccess('Abonné retiré');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    }
  }

  return (
    <GrcLayout active="communication">
      <div className="border-b border-border px-4 py-2">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
          <GrcTab onClick={() => navigate('/grc/messages')}>Boîte citoyenne</GrcTab>
          <GrcTab onClick={() => navigate('/grc/messagerie')}>Messagerie interne</GrcTab>
          <GrcTab onClick={() => navigate('/grc/alertes')}>Alertes municipales</GrcTab>
          <GrcTab onClick={() => navigate('/grc/newsletters')}>Newsletter</GrcTab>
          <GrcTab active>Passerelle SMS</GrcTab>
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-[900px] px-4 py-5 animate-fade-in">
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={MessageSquare} accent="tech" />
          <div>
            <SectionEyebrow as="span">GRC &amp; Communication</SectionEyebrow>
            <h1 className="text-xl font-semibold text-ink">Passerelle SMS d’alerte</h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={X} className="mb-4">{error}</Alert>}
        {success && <Alert variant="success" icon={Check} className="mb-4">{success}</Alert>}

        {loading || !config ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : (
          <div className="space-y-4">
            {/* État courant */}
            <Card variant="glass" padded>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">
                    Canal SMS : {config.actif ? <Badge variant="success">Activé</Badge> : <Badge variant="neutral">Désactivé</Badge>}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Mode : {config.modeCompte === 'commune' ? 'ma clé (prix coûtant)' : 'passerelle VPC (clé en main)'}
                    {' · '}Expéditeur : « {config.expediteur} »
                    {config.modeCompte === 'passerelle' && <> · Solde : {config.soldeSms} crédits</>}
                  </p>
                </div>
                <Button
                  type="button"
                  variant={config.actif ? 'secondary' : 'primary'}
                  size="sm"
                  disabled={saving}
                  onClick={() => save({ actif: !config.actif }, config.actif ? 'Canal SMS désactivé' : 'Canal SMS activé')}
                >
                  {config.actif ? 'Désactiver' : 'Activer'}
                </Button>
              </div>
            </Card>

            {/* CAS 1 — désactivé */}
            <Card padded>
              <div className="flex items-start gap-3">
                <Ban className="mt-0.5 h-5 w-5 text-ink-muted" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-ink">Cas 1 — Vous n’avez pas besoin du SMS</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Si la commune utilise déjà une autre solution d’alerte, laissez le canal désactivé.
                    Il n’apparaîtra pas dans les alertes et rien ne sera facturé. Vous pourrez l’activer plus tard.
                  </p>
                </div>
              </div>
            </Card>

            {/* CAS 2 — ma clé */}
            <Card padded className={mode === 'commune' ? 'ring-1 ring-tech' : ''}>
              <div className="flex items-start gap-3">
                <KeyRound className="mt-0.5 h-5 w-5 text-tech" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink">Cas 2 — Votre propre clé smsmode (prix coûtant)</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Créez un compte sur smsmode.com (10 min), récupérez votre clé API et renseignez-la ici.
                    Vous payez smsmode en direct : <strong>aucune marge, prix coûtant réel</strong>, et vous profitez vous-même du tarif dégressif selon vos volumes. La clé est chiffrée.
                  </p>
                  <div className="mt-3 space-y-3">
                    <Field label="Expéditeur affiché (≤ 11 caractères)" htmlFor="sms-exp-c">
                      <Input id="sms-exp-c" value={expediteur} maxLength={11}
                        onChange={(e) => setExpediteur(e.target.value)} placeholder="Mairie" />
                    </Field>
                    <Field label={config.cleConfiguree ? 'Remplacer la clé API (laisser vide = inchangée)' : 'Clé API smsmode'} htmlFor="sms-key">
                      <Input id="sms-key" type="password" value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={config.cleConfiguree ? '•••••••• (une clé est enregistrée)' : 'Collez votre clé API'} />
                    </Field>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="primary" size="sm" disabled={saving}
                        onClick={() => save(
                          { modeCompte: 'commune', expediteur, ...(apiKey ? { apiKey } : {}) },
                          'Configuration « ma clé » enregistrée'
                        )}>
                        Utiliser ma clé
                      </Button>
                      {config.cleConfiguree && (
                        <Button type="button" variant="ghost" size="sm" disabled={saving}
                          onClick={() => save({ apiKey: null }, 'Clé supprimée')}>
                          Supprimer la clé
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* CAS 3 — passerelle VPC */}
            <Card padded className={mode === 'passerelle' ? 'ring-1 ring-tech' : ''}>
              <div className="flex items-start gap-3">
                <Server className="mt-0.5 h-5 w-5 text-tech" aria-hidden="true" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink">Cas 3 — Passerelle VPC (clé en main)</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    Rien à installer : VPC envoie les SMS pour vous. Vous fonctionnez sur un solde de crédits.
                    Prix : <strong>{(config.prixUnitaireEur + config.surcoutPasserelleEur).toFixed(3).replace('.', ',')} €/SMS</strong>, tout compris (tarif fixe, quel que soit le volume).
                    {!config.passerelleVpcDisponible && (
                      <span className="mt-1 block font-semibold text-urgent">
                        Passerelle VPC non disponible côté serveur (SMS_ENABLED / SMS_API_KEY à configurer).
                      </span>
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <Field label="Expéditeur affiché (≤ 11 caractères)" htmlFor="sms-exp-p">
                      <Input id="sms-exp-p" value={expediteur} maxLength={11}
                        onChange={(e) => setExpediteur(e.target.value)} placeholder="Mairie" />
                    </Field>
                    <Button type="button" variant="primary" size="sm" disabled={saving}
                      onClick={() => save({ modeCompte: 'passerelle', expediteur }, 'Passerelle VPC activée')}>
                      Utiliser la passerelle VPC
                    </Button>
                  </div>

                  {/* Solde + recharge (super_admin) */}
                  <div className="mt-4 rounded-lg border border-line bg-surface-muted/40 p-3">
                    <p className="text-xs text-ink-muted">
                      Solde actuel : <span className="font-semibold text-ink">{config.soldeSms}</span> crédits
                      {' · '}consommé : {config.totalConsomme}
                    </p>
                    {isSuperAdmin ? (
                      <div className="mt-2 flex items-end gap-2">
                        <Field label="Créditer des SMS (amorçage / recharge)" htmlFor="sms-rech">
                          <Input id="sms-rech" type="number" min={1} value={rechargeNb}
                            onChange={(e) => setRechargeNb(e.target.value)} placeholder="1000" />
                        </Field>
                        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={recharger}>
                          Créditer
                        </Button>
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-ink-muted">
                        Le crédit initial est posé au démarrage. Pour recharger, contactez One For All Distribution.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Abonnés SMS — gestion mairie (voir / retirer un mauvais numéro) */}
            <Card padded>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-ink">
                  Abonnés SMS ({abonnes.filter((a) => a.statut === 'actif').length} actif·s)
                </p>
                <Button type="button" variant="ghost" size="sm" onClick={() => void reloadAbonnes()}>
                  Rafraîchir
                </Button>
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Les citoyens s’abonnent depuis leur compte. Retirez un numéro erroné pour ne pas
                envoyer (ni payer) dans le vide.
              </p>
              {abonnes.length === 0 ? (
                <p className="mt-3 text-xs text-ink-muted">Aucun abonné pour l’instant.</p>
              ) : (
                <ul className="mt-3 divide-y divide-line">
                  {abonnes.map((a) => (
                    <li key={a.id} className="flex items-center justify-between py-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-ink">{a.telephone}</span>
                        {a.statut !== 'actif' && (
                          <Badge variant="neutral" size="sm" className="ml-2">désabonné</Badge>
                        )}
                        <span className="ml-2 text-xs text-ink-muted">
                          {a.statut === 'actif' || !a.desabonneAt
                            ? `inscrit le ${new Date(a.consentAt).toLocaleDateString('fr-FR')}`
                            : `STOP le ${new Date(a.desabonneAt).toLocaleDateString('fr-FR')}`}
                        </span>
                      </div>
                      {a.statut === 'actif' && (
                        <Button type="button" variant="ghost" size="sm"
                          onClick={() => void handleDesabonner(a.id)}>
                          Retirer
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <SmsSecteursPanel />

            {config && <SmsFacturationPanel config={config} />}
          </div>
        )}
      </main>
    </GrcLayout>
  );
}

// ============================================================================
// Panneau « Secteurs d'alerte » (segmentation PCS) — gestion mairie
// ============================================================================
function SmsSecteursPanel() {
  const [secteurs, setSecteurs] = useState<Array<{ id: string; libelle: string; ordre: number; actif: boolean; nbAbonnes: number }>>([]);
  const [nouveau, setNouveau] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cartes, setCartes] = useState<Array<{ id: string; libelle: string; typeFichier: 'pdf' | 'image'; url: string; tailleBytes: number }>>([]);
  const [carteBusy, setCarteBusy] = useState(false);

  async function reload() {
    try { setSecteurs((await api.grc.sms.secteurs.list(true)).secteurs); } catch { /* silencieux */ }
    try { setCartes((await api.grc.sms.secteurs.cartes.list()).cartes); } catch { /* silencieux */ }
  }
  useEffect(() => { void reload(); }, []);

  async function importerCarte(file: File) {
    setCarteBusy(true); setErr(null);
    try {
      const prep = await compressImageIfNeeded(file); // PDF renvoyé tel quel
      await api.grc.sms.secteurs.cartes.importer(prep, file.name);
      await reload();
    } catch { setErr('Import impossible (format PDF/JPG/PNG, 10 Mo max, 10 cartes max).'); }
    finally { setCarteBusy(false); }
  }

  async function supprimerCarte(id: string) {
    setCarteBusy(true); setErr(null);
    try { await api.grc.sms.secteurs.cartes.supprimer(id); await reload(); }
    catch { setErr('Suppression impossible.'); }
    finally { setCarteBusy(false); }
  }

  async function ajouter() {
    const libelle = nouveau.trim();
    if (!libelle) return;
    setBusy(true); setErr(null);
    try {
      await api.grc.sms.secteurs.creer({ libelle });
      setNouveau('');
      await reload();
    } catch { setErr('Impossible de créer ce secteur (déjà existant ?).'); }
    finally { setBusy(false); }
  }

  async function toggleActif(id: string, actif: boolean) {
    setBusy(true); setErr(null);
    try { await api.grc.sms.secteurs.maj(id, { actif: !actif }); await reload(); }
    catch { setErr('Modification impossible.'); }
    finally { setBusy(false); }
  }

  return (
    <Card className="p-4">
      <SectionEyebrow>Segmentation</SectionEyebrow>
      <h3 className="mb-1 text-sm font-semibold text-ink">Secteurs d'alerte</h3>
      <p className="mb-3 text-xs text-ink-muted">
        Définissez des zones (quartiers, rues, secteurs à risque). Les habitants choisissent
        leurs secteurs en s'abonnant ; vous pourrez cibler une alerte sur un ou plusieurs secteurs.
        Un abonné sans secteur reçoit uniquement les alertes envoyées à toute la commune.
      </p>

      {err && <Alert variant="error" className="mb-2">{err}</Alert>}

      <div className="mb-3 flex items-end gap-2">
        <div className="flex-1">
        <Field label="Nouveau secteur" htmlFor="sec-new">
          <Input id="sec-new" value={nouveau} maxLength={120}
            placeholder="Ex. Centre-bourg"
            onChange={(e) => setNouveau(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void ajouter(); }} />
        </Field>
        </div>
        <Button size="sm" onClick={() => void ajouter()} disabled={busy || !nouveau.trim()}>Ajouter</Button>
      </div>

      {secteurs.length === 0 ? (
        <p className="text-sm text-ink-subtle">Aucun secteur défini pour l'instant.</p>
      ) : (
        <ul className="divide-y divide-border">
          {secteurs.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <span className={`text-sm font-medium ${s.actif ? 'text-ink' : 'text-ink-subtle line-through'}`}>{s.libelle}</span>
                <span className="ml-2 text-xs text-ink-muted">{s.nbAbonnes} abonné·s</span>
                {!s.actif && <Badge variant="neutral" size="sm" className="ml-2">inactif</Badge>}
              </div>
              <Button variant="ghost" size="sm" disabled={busy}
                onClick={() => void toggleActif(s.id, s.actif)}>
                {s.actif ? 'Désactiver' : 'Réactiver'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Cartes de référence (PDF / image) */}
      <div className="mt-5 border-t border-border pt-4">
        <h4 className="mb-1 text-sm font-semibold text-ink">Cartes de référence</h4>
        <p className="mb-2 text-xs text-ink-muted">
          Importez le plan de vos secteurs (issu de votre PCS/DICRIM ou d'un autre logiciel) —
          PDF, JPG ou PNG. Il sert d'aide visuelle pour vous et pour les habitants qui choisissent
          leur secteur. 10 cartes maximum, 10 Mo par fichier (images compressées automatiquement).
        </p>

        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-ink hover:border-accent/50 ${carteBusy ? 'pointer-events-none opacity-50' : ''}`}>
          {carteBusy ? 'Import en cours…' : 'Importer une carte'}
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={carteBusy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void importerCarte(f); e.target.value = ''; }}
          />
        </label>

        {cartes.length > 0 && (
          <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {cartes.map((c) => (
              <li key={c.id} className="rounded-lg border border-border p-2">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs font-medium text-ink">{c.libelle}</span>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-danger-fg hover:underline disabled:opacity-50"
                    disabled={carteBusy}
                    onClick={() => void supprimerCarte(c.id)}
                  >
                    Supprimer
                  </button>
                </div>
                {c.typeFichier === 'image' && c.url ? (
                  <a href={c.url} target="_blank" rel="noreferrer">
                    <img src={c.url} alt={c.libelle} className="max-h-40 w-full rounded object-contain" />
                  </a>
                ) : (
                  <a href={c.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent-fg hover:underline">
                    Ouvrir le PDF
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
// ============================================================================
// Panneau « SMS & facturation » (mode passerelle) — journal, factures, params
// ============================================================================
function SmsFacturationPanel({ config }: { config: Config }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [journal, setJournal] = useState<Array<{ id: string; telephone: string; nbSegments: number; coutEur: number; statut: string; statutRemise: string; createdAt: string }>>([]);
  const [stats, setStats] = useState<{ total: number; remis: number; echec: number; enCours: number; inconnu: number; tauxRemisePct: number | null } | null>(null);
  const [factures, setFactures] = useState<Array<{ id: string; type: string; periode: string | null; nbSms: number; montantTtc: number; factureId: string | null; createdAt: string }>>([]);
  // Valeurs RÉELLES de la commune (chargées depuis la config, plus de défauts trompeurs).
  const [solde, setSolde] = useState(config.soldeSms);
  const [seuil, setSeuil] = useState(String(config.seuilAlerteSms ?? 100));
  const [pack, setPack] = useState(String(config.packRechargeSms ?? 500));
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      const j = await api.grc.sms.facturation.journal(from, to);
      setJournal(j.journal);
      try { setStats(await api.grc.sms.facturation.statsRemise(from, to)); } catch { /* stats optionnelles */ }
      if (config.modeCompte === 'passerelle') {
        const f = await api.grc.sms.facturation.factures();
        setFactures(f.factures);
      }
    } catch { /* silencieux */ }
  }
  useEffect(() => { void reload(); /* au montage */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveParams() {
    setBusy(true); setMsg(null);
    try {
      await api.grc.sms.facturation.setConfig({
        seuilAlerteSms: parseInt(seuil, 10) || 100,
        packRechargeSms: parseInt(pack, 10) || 500,
      });
      setMsg('Paramètres enregistrés.');
    } catch (e) { setMsg(e instanceof ApiException ? e.payload.error : 'Erreur'); }
    finally { setBusy(false); }
  }


  // 2 décimales par défaut, mais jusqu'à 3 pour ne pas arrondir le coût unitaire
  // réel (0,065 €/SMS) à 0,07 dans le journal.
  const fmtEuro = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

  return (
    <Card padded>
      <SectionEyebrow as="span">SMS &amp; facturation</SectionEyebrow>
      <h2 className="mb-3 font-display text-base font-semibold text-ink">Consommation &amp; refacturation</h2>
      {msg && <Alert variant="info" className="mb-3">{msg}</Alert>}

      {/* Compteur SMS de la commune (crédit piloté par OFAD depuis l'admin). */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 rounded-r-lg border border-border/60 bg-surface/20 p-3">
        <div>
          <p className="text-xs text-ink-subtle">Compteur SMS de la commune</p>
          <p className="text-2xl font-semibold text-ink">{solde}<span className="ml-1 text-sm font-normal text-ink-muted">crédits</span></p>
          <p className="mt-1 text-2xs text-ink-subtle">Le crédit est ajouté par One For All Distribution après réception de votre paiement.</p>
        </div>
        {config.modeCompte === 'passerelle' && <DemandeRechargeButton />}
      </div>

      {config.modeCompte === 'commune' && (
        <Alert variant="info" className="mb-4">
          Vous utilisez votre propre clé SMS : aucune facturation par la plateforme. Ce compteur vous sert d'estimation.
        </Alert>
      )}

      {/* Paramètres */}
      <div className="mb-5 grid grid-cols-2 gap-3">
        <Field label="Seuil d'alerte (crédits)" htmlFor="sf-seuil">
          <Input id="sf-seuil" type="number" min={0} value={seuil} onChange={(e) => setSeuil(e.target.value)} />
        </Field>
        <Field label="Pack de recharge (SMS)" htmlFor="sf-pack">
          <Input id="sf-pack" type="number" min={1} value={pack} onChange={(e) => setPack(e.target.value)} />
        </Field>
      </div>
      <Button variant="ghost" size="sm" className="mb-5" onClick={() => void saveParams()} disabled={busy}>Enregistrer les paramètres</Button>

      {/* Historique factures */}
      {config.modeCompte === 'passerelle' && (
        <>
          <h3 className="mb-2 text-sm font-semibold text-ink">Factures SMS émises</h3>
          {factures.length === 0 ? (
            <p className="mb-4 text-sm text-ink-subtle">Aucune facture SMS pour l'instant.</p>
          ) : (
            <ul className="mb-5 divide-y divide-border text-sm">
              {factures.map((f) => (
                <li key={f.id} className="flex items-center justify-between py-2">
                  <span>
                    <Badge>{f.type === 'mensuel' ? 'Mensuelle' : 'Recharge'}</Badge>
                    <span className="ml-2 text-ink-muted">{f.periode ?? new Date(f.createdAt).toLocaleDateString('fr-FR')} · {f.nbSms} SMS</span>
                  </span>
                  <span className="font-medium text-ink">{fmtEuro(f.montantTtc)} €</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Journal de consommation */}
      <h3 className="mb-2 text-sm font-semibold text-ink">Journal de consommation</h3>
      <div className="mb-2 flex flex-wrap items-end gap-2">
        <Field label="Du" htmlFor="sf-from"><Input id="sf-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="Au" htmlFor="sf-to"><Input id="sf-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button variant="ghost" size="sm" onClick={() => void reload()}>Filtrer</Button>
      </div>
      {/* Taux de remise (DLR) sur la période */}
      {stats && stats.total > 0 && (
        <div className="mb-3 rounded-lg border border-border bg-surface-subtle p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-ink">Remise (accusés opérateur)</span>
            {stats.tauxRemisePct !== null ? (
              <span className="text-sm font-semibold text-ink">{stats.tauxRemisePct}% délivrés</span>
            ) : (
              <span className="text-xs text-ink-subtle">accusés en attente…</span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant="success" size="sm">{stats.remis} remis</Badge>
            <Badge variant="urgent" size="sm">{stats.echec} échec·s</Badge>
            <Badge variant="info" size="sm">{stats.enCours} en cours</Badge>
            {stats.inconnu > 0 && <Badge variant="neutral" size="sm">{stats.inconnu} inconnu·s</Badge>}
          </div>
        </div>
      )}
      {journal.length === 0 ? (
        <p className="text-sm text-ink-subtle">Aucun SMS sur la période.</p>
      ) : (
        <ul className="max-h-64 divide-y divide-border overflow-auto text-xs">
          {journal.map((j) => {
            const rv =
              j.statutRemise === 'remis' ? 'success'
              : j.statutRemise === 'echec' ? 'urgent'
              : j.statutRemise === 'en_cours' ? 'info'
              : 'neutral';
            const rlabel =
              j.statutRemise === 'remis' ? 'remis'
              : j.statutRemise === 'echec' ? 'échec'
              : j.statutRemise === 'en_cours' ? 'en cours'
              : 'inconnu';
            return (
              <li key={j.id} className="flex items-center justify-between gap-2 py-1.5">
                <span className="min-w-0 truncate text-ink-muted">{new Date(j.createdAt).toLocaleString('fr-FR')} · {j.telephone}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge variant={rv} size="sm">{rlabel}</Badge>
                  <span className={j.statut === 'sent' ? 'text-ink' : 'text-danger-fg'}>{j.nbSegments} seg · {fmtEuro(j.coutEur)} €</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── Bouton « Demander une recharge » (commune, mode passerelle) ──────────────
function DemandeRechargeButton() {
  const [open, setOpen] = useState(false);
  const [nb, setNb] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function envoyer() {
    if (!file) { setMsg('Le justificatif de paiement est obligatoire.'); return; }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      if (nb) fd.append('nbSmsDemande', nb);
      if (message) fd.append('message', message);
      await api.grc.sms.facturation.demanderRecharge(fd);
      setMsg('Demande envoyée. One For All Distribution la traitera après vérification du paiement.');
      setNb(''); setMessage(''); setFile(null);
    } catch (e) { setMsg(e instanceof ApiException ? e.payload.error : 'Erreur'); }
    finally { setBusy(false); }
  }

  if (!open) {
    return <Button variant="primary" size="sm" onClick={() => setOpen(true)}>Demander une recharge</Button>;
  }
  return (
    <div className="w-full rounded-r-lg border border-border/60 bg-surface/30 p-3">
      <p className="mb-2 text-sm font-semibold text-ink">Demander une recharge</p>
      {msg && <Alert variant="info" className="mb-2">{msg}</Alert>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Nombre de SMS souhaité (indicatif)" htmlFor="dr-nb">
          <Input id="dr-nb" type="number" min={1} value={nb} onChange={(e) => setNb(e.target.value)} placeholder="ex. 500" />
        </Field>
        <Field label="Message (facultatif)" htmlFor="dr-msg">
          <Input id="dr-msg" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Réglé par virement le…" />
        </Field>
      </div>
      <div className="mt-2">
        <label htmlFor="dr-file" className="mb-1 block text-sm text-ink">Justificatif de paiement</label>
        <input id="dr-file" type="file" accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-r-lg file:border-0 file:bg-surface file:px-3 file:py-2 file:text-sm file:text-ink" />
        <p className="mt-1 text-2xs text-ink-subtle">Justificatif obligatoire : ordre de virement, preuve de paiement, facture acquittée…</p>
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="primary" size="sm" onClick={() => void envoyer()} disabled={busy || !file}>Envoyer la demande</Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
      </div>
    </div>
  );
}
